import type { Ref } from "vue";

import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CoursePackageWithDetailsView } from "~/business-areas/course-package/composables/course-packages.hook";
import type { CourseSessionView } from "~/business-areas/course-session/course-session.model";
import type { CourseRatingItemView } from "~~/model/course.model";

export function useCoursePageMeta({
  course,
  packages,
  nextSession,
  ratingsWithComment,
}: {
  course: Ref<CoursePageView>;
  packages: Ref<CoursePackageWithDetailsView[]>;
  nextSession: Ref<CourseSessionView | undefined>;
  ratingsWithComment: Ref<CourseRatingItemView[]>;
}) {
  const {
    public: {
      apps: { websiteBaseUrl },
    },
  } = useRuntimeConfig();

  const { t } = useI18n();

  const title = computed(() =>
    t(`course.page.title`, {
      title: course.value?.title,
      teacherFullName: course.value?.teacher.full_name,
    }),
  );

  useSeoMeta({
    title,
    description: () => course.value?.description,
    ogImage: () =>
      course.value?.og_image_url ??
      `${websiteBaseUrl}/images/brand/og-image.jpg`,
    ogTitle: title,
    ogImageAlt: title,
    twitterCard: "summary_large_image",
    twitterTitle: title,
    twitterDescription: () =>
      `${course.value?.teacher.full_name}, ${course.value?.teacher.job}`,
    twitterImage: () => course.value?.og_image_url,
    robots: course.value?.indexed ? undefined : "noindex",
  });

  useSchemaOrg([
    defineCourse(
      computed(() => {
        return {
          "@id": `${websiteBaseUrl}/courses/${course.value?.slug}#schema`,
          "@type": "Course",
          name: course.value?.title,
          provider: {
            "@id": `${websiteBaseUrl}/#identity`,
          },
          description: course.value?.description,
          image: course.value?.og_image_url ?? undefined,
          author: definePerson({
            name: course.value?.teacher.full_name ?? "",
            image: course.value?.teacher.avatar_url,
            url: `${websiteBaseUrl}/trainers/${course.value?.teacher.slug}`,
          }),
          ...(nextSession.value
            ? {
                hasCourseInstance: {
                  "@type": "CourseInstance",
                  courseMode: nextSession.value?.location_type,
                  courseSchedule: {
                    endDate: nextSession.value?.end_date,
                    startDate: nextSession.value?.start_date,
                    repeatCount: 1,
                    repeatFrequency: "daily",
                  },
                },
              }
            : {}),
          ...(packages.value.length > 0
            ? {
                offers: {
                  "@type": "AggregateOffer",
                  priceCurrency: packages.value[0]?.price.currency,
                  lowPrice: packages.value[0]?.price.amount,
                  highPrice: packages.value.at(-1)!.price.amount,
                  offerCount: packages.value.length,
                  category: course.value.tags[0]?.slug,
                },
              }
            : {}),
          ...(course.value?.ratings
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: course.value.ratings.avg,
                  reviewCount: course.value.ratings.count,
                },
              }
            : {}),
          review: ratingsWithComment.value.map((rating) => {
            return {
              "@type": "Review",
              author: {
                "@type": "Person",
                name: rating.author.full_name,
              },
              datePublished: rating.created_at,
              reviewBody: rating.comment,
              reviewRating: {
                "@type": "Rating",
                bestRating: "5",
                ratingValue: rating.global_satisfaction,
                worstRating: "1",
              },
            };
          }),
        };
      }),
    ),
    ...(course.value?.video_url
      ? [
          defineVideo({
            name: title,
            description: course.value.description,
            contentUrl: course.value.video_url,
            thumbnailUrl: course.value.og_image_url,
            width: 1920,
            height: 1080,
            inLanguage: course.value.languages[0],
            uploadDate: course.value.created_at ?? undefined,
          }),
        ]
      : []),
  ]);
}
